import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HandCoins, Loader2, AlertCircle, RefreshCw, Plus, ChevronLeft, Inbox } from "lucide-react";
import {
  FUNDING_STATUS_LABEL, FUNDING_STATUS_TONE,
  formatSAR, formatArabicDateTime,
} from "./fundingLabels";

/**
 * FundingRequestsList — Wave 3C
 * Donor-scoped list backed by GET /api/funding/requests/mine.
 * Read-only presentation. No mock data. No plan actions here.
 */
export default function FundingRequestsList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/funding/requests/mine");
      setRows(Array.isArray(data.requests) ? data.requests : []);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <DashboardLayout>
      <div data-testid="funding-requests-list-page">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">الجهة الممولة</div>
            <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900 flex items-center gap-2">
              <HandCoins className="h-6 w-6 text-amber-700" strokeWidth={1.75} /> طلبات التمويل
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              تابع جميع طلباتك: المسودات، الطلبات المُرسَلة، والحالات الجارية.
            </p>
          </div>
          <Button
            onClick={() => navigate("/dashboard/funding/requests/new")}
            className="bg-teal-700 hover:bg-teal-800 whitespace-nowrap"
            data-testid="btn-new-funding-request"
          >
            <Plus className="h-4 w-4 ms-2" /> تقديم طلب جديد
          </Button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24" data-testid="list-loading">
            <Loader2 className="h-7 w-7 animate-spin text-teal-700" />
          </div>
        )}

        {!loading && error && (
          <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200" data-testid="list-error">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-rose-700 shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-heading font-bold text-rose-900">تعذّر تحميل طلبات التمويل</h3>
                <p className="text-sm text-rose-800 mt-1">{error}</p>
                <Button variant="outline" onClick={load} className="mt-3" data-testid="list-retry">
                  <RefreshCw className="h-4 w-4 ms-2" /> إعادة المحاولة
                </Button>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && rows && rows.length === 0 && (
          <div
            className="p-10 rounded-2xl bg-white border border-cream-200 card-shadow text-center"
            data-testid="list-empty"
          >
            <Inbox className="h-8 w-8 mx-auto text-slate-400" />
            <h3 className="mt-3 font-heading font-bold text-slate-900">لا توجد طلبات تمويل بعد</h3>
            <p className="text-sm text-slate-600 mt-1">
              ابدأ بتقديم طلبك الأول وسنقوم بمراجعته وإعداد خطة تنفيذ مناسبة.
            </p>
            <Button
              onClick={() => navigate("/dashboard/funding/requests/new")}
              className="mt-4 bg-teal-700 hover:bg-teal-800"
              data-testid="btn-empty-new-request"
            >
              <Plus className="h-4 w-4 ms-2" /> تقديم طلب جديد
            </Button>
          </div>
        )}

        {!loading && !error && rows && rows.length > 0 && (
          <>
            {/* Desktop table */}
            <div
              className="hidden md:block rounded-2xl bg-white border border-cream-200 card-shadow overflow-hidden"
              data-testid="requests-table-wrap"
            >
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs">
                  <tr>
                    <th className="text-start px-4 py-3 font-semibold">رقم الطلب</th>
                    <th className="text-start px-4 py-3 font-semibold">هدف التمويل</th>
                    <th className="text-start px-4 py-3 font-semibold">القيمة</th>
                    <th className="text-start px-4 py-3 font-semibold">الحالة</th>
                    <th className="text-start px-4 py-3 font-semibold">آخر تحديث</th>
                    <th className="text-start px-4 py-3 font-semibold sr-only">فتح</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/dashboard/funding/requests/${r.id}`)}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      data-testid={`row-request-${r.id}`}
                    >
                      <td className="px-4 py-3 font-semibold text-slate-900">
                        {r.request_number || (
                          <span className="text-slate-400 italic">مسودة</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700 max-w-[320px] truncate">
                        {r.funding_goal || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {formatSAR(r.estimated_amount_or_value)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {formatArabicDateTime(r.updated_at || r.created_at)}
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        <ChevronLeft className="h-4 w-4" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3" data-testid="requests-cards-wrap">
              {rows.map((r) => (
                <Link
                  key={r.id}
                  to={`/dashboard/funding/requests/${r.id}`}
                  className="block p-4 rounded-2xl bg-white border border-cream-200 card-shadow hover:border-teal-300 transition-colors"
                  data-testid={`card-request-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">
                        {r.request_number || <span className="text-slate-400 italic">مسودة</span>}
                      </div>
                      <div className="text-sm text-slate-700 mt-1 line-clamp-2">
                        {r.funding_goal || "—"}
                      </div>
                    </div>
                    <StatusPill status={r.status} />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                    <span>{formatSAR(r.estimated_amount_or_value)}</span>
                    <span>{formatArabicDateTime(r.updated_at || r.created_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatusPill({ status }) {
  const label = FUNDING_STATUS_LABEL[status] || status || "—";
  const tone = FUNDING_STATUS_TONE[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold ${tone}`}
      data-testid={`status-${status || "unknown"}`}
    >
      {label}
    </span>
  );
}
